/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

/** @typedef {import('./types').Field} Field */
/** @typedef {import('./types').Table} Table */

import { useState } from 'react';

import { DocumentViewerLayout } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/document-viewer-layout';
import { structuredExtractionStore } from 'stores';
import { createEntityManager } from 'App/Pages/Project/IdpApplication/idp-project/utils/structured-extraction-helpers';
import { getExtractionFieldNameInvalidText } from 'App/Pages/Project/IdpApplication/idp-project/utils/utils';

import { HeaderControls } from './header-controls';
import { ViewRenderer } from './view-renderer';
import { FooterControls } from './footer-controls';
import * as Styled from './ExtractedResults.styled';

export const ExtractedResults = ({ allDocuments, isPdfViewerVisible, onTogglePdfViewer }) => {
  const [selectedView, setSelectedView] = useState(/** @type {'forms'|'tables'} */ ('forms'));
  const [changedFields, setChangedFields] = useState(new Map());
  const [changedTables, setChangedTables] = useState(new Map());
  const [nameValidation, setNameValidation] = useState({});

  const { documentForExtraction, structuredExtractionResults } = structuredExtractionStore;
  const fieldsData = structuredExtractionResults?.fields ?? [];
  const tablesData = structuredExtractionResults?.tables ?? [];

  const forms = createEntityManager(fieldsData, changedFields, setChangedFields);
  const tables = createEntityManager(tablesData, changedTables, setChangedTables);

  /**
   * @param {ReturnType<typeof createEntityManager>} entity
   * @returns {(id: string, active: boolean) => void}
   */
  const handleToggleActive = (entity) => (id, active) => {
    const list = entity.data;
    const setChanged = entity.setChanged;
    const item = list.find((el) => el.id === id);
    if (item) {
      const updated = new Map(entity.changed);
      const prev = updated.get(id) || {};
      updated.set(id, { ...item, ...prev, active });
      setChanged(updated);
    }
  };

  /**
   * @param {ReturnType<typeof createEntityManager>} entity
   * @returns {(active: boolean) => void}
   */
  const handleToggleAllActive = (entity) => (active) => {
    const list = entity.data;
    const setChanged = entity.setChanged;
    setChanged(
      new Map(
        list.map((el) => {
          const prev = entity.changed.get(el.id) || {};
          return [el.id, { ...el, ...prev, active }];
        })
      )
    );
  };

  /**
   * @param {ReturnType<typeof createEntityManager>} entity
   * @returns {(id: string, value: string) => void}
   */
  const handleNameChange = (entity) => (id, value) => {
    const list = entity.data;
    const setChanged = entity.setChanged;
    const item = list.find((el) => el.id === id);
    if (item) {
      const updated = new Map(entity.changed);
      const prev = updated.get(id) || {};
      updated.set(id, { ...item, ...prev, replacedName: value });
      setChanged(updated);
      const invalidText = !value
        ? 'Name is required.'
        : getExtractionFieldNameInvalidText(value, [...fieldsData, ...tablesData]);
      setNameValidation((prev) => ({ ...prev, [id]: invalidText }));
    }
  };

  const handleConfirm = async () => {
    const allChanged = [...forms.getChanged(), ...tables.getChanged()];
    await structuredExtractionStore.bulkUpdateExtractionFields(allChanged);
    setChangedFields(new Map());
    setChangedTables(new Map());
  };

  const handleViewChange = ({ name }) => {
    setSelectedView(name);
  };

  const leftPanel = (
    <Styled.ResultsContainer>
      <HeaderControls
        isPdfViewerVisible={isPdfViewerVisible}
        onTogglePdfViewer={onTogglePdfViewer}
        selectedView={selectedView}
        onViewChange={handleViewChange}
      />
      <ViewRenderer
        selectedView={selectedView}
        forms={{
          fields: /** @type {Field[]} */ (forms.getMerged()),
          onToggleActive: handleToggleActive(forms),
          onToggleAllActive: handleToggleAllActive(forms),
          onNameChange: handleNameChange(forms),
          nameValidation: nameValidation
        }}
        tables={{
          tables: /** @type {Table[]} */ (tables.getMerged()),
          onToggleActive: handleToggleActive(tables),
          onToggleAllActive: handleToggleAllActive(tables),
          onNameChange: handleNameChange(tables)
        }}
      />
      <FooterControls
        totalFields={forms.getTotal()}
        totalTables={tables.getTotal()}
        selectedFields={forms.getChanged()}
        selectedTables={tables.getChanged()}
        selectedCount={forms.getSelectedCount()}
        selectedTableCount={tables.getSelectedCount()}
        onConfirm={handleConfirm}
      />
    </Styled.ResultsContainer>
  );

  return (
    <DocumentViewerLayout
      leftPanelContent={leftPanel}
      document={documentForExtraction}
      allDocuments={allDocuments}
      isRightPanelVisible={isPdfViewerVisible}
    />
  );
};
